<template>
  <Loader
    :value="promises.fetchOne"
    :message="$t('loading', { resource: $tc('addonShop').toLowerCase() }) + '...'"
    class="shop"
  >
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #right>
          <div class="row wrap g-1">
            <DropdownComponent :close-on-click="false" class="shop__previews">
              <ButtonComponent :label="$t('showShop')" :icon="['fal', 'external-link']" />
              <template #dropdown>
                <template v-if="item.categories && item.categories.length">
                  <ButtonComponent
                    :label="$t('shopLinkPresentation')"
                    :icon="['fal', 'eye']"
                    tag="a"
                    :href="item.url"
                    target="_blank"
                  />
                  <ButtonComponent
                    tag="a"
                    :label="$t('shopLinkSigning')"
                    :icon="['fal', 'hand-pointer']"
                    :href="contract.signUrl && '//' + contract.signUrl"
                    :disabled="!contract.status"
                    target="_blank"
                  />
                </template>
                <Alert v-else class="shop__previews-alert">
                  <span>{{ $t('shopPresentationPreviewAlert') }}</span>
                </Alert>
              </template>
            </DropdownComponent>
            <TaskButtonList v-model="item.tasks" :reference-id="id" />
            <Actions placement="shop" :query="{ shop: id }">
              <template #bottom>
                <ButtonComponent
                  v-if="$store.getters.hasAccess('admin') && contract.status === 'draft'"
                  :label="$t('delete')"
                  @click="removeDialog = true"
                  :icon="['fal', 'times']"
                />
                <ButtonComponent
                  v-if="contract.status === 'unsigned'"
                  :label="$t('reinvite')"
                  :icon="['fal', 'envelope']"
                  :promise="resendPromise"
                  @click.native="resend"
                />
              </template>
            </Actions>
            <ButtonComponent
              v-if="$store.getters['Project/hasRole']('shop.manager', project) && contract.status !== 'accepted'"
              tag="router-link"
              :label="$t('edit')"
              theme="primary"
              :icon="['fal', 'pen']"
              :to="{ name: 'shop-mutate', params: { id } }"
            />
          </div>
        </template>
      </HeaderComponent>
      <div class="shop__content">
        <Summary data-widget="summary" />
        <Shop />
        <Signing @refetch="fetch" />
      </div>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$path('reference.name', item)"
        :action="remove"
        @onSuccess="
          $router.push({
            name: 'project-dashboard',
            params: { id: item.project.id },
          })
        "
        :resource="$tc('addonShop')"
      />
    </div>
  </Loader>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { mapi18n } from '@/i18n'
import { mergeDeepRight } from 'ramda'
import ContractApi from '@/api/Contract'

import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import RemoveComponent from '@/components/Remove'
import { DropdownComponent } from 'vue-elder-dropdown'
import Widgets from './Widgets'
import { AlertComponent as Alert } from 'vue-elder-alert'
import Actions from '@/components/Actions'
import TaskButtonList from '@/components/Task/ButtonList'

const BreadcrumbRouteMap = {
  Residential: { name: 'residential-edit' },
  ResidentialTemplate: { name: 'residential-template-edit' },
}

export default {
  props: {
    id: String,
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      removeDialog: false,
      resendPromise: null,
    }
  },
  computed: {
    ...mapState('Shop', {
      item: 'item',
      promises: 'promises',
      contract: state => state.item.contract || {},
      project: state => state.item.project,
    }),
    ...mapi18n('labels', {
      loading() {
        return this.$tc('loading') + '...'
      },
      shop: ['addonShop', 1],
    }),
    breadcrumb() {
      if (!this.item.id) return []
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.$path('item.project.name'),
          action: {
            name: 'project-dashboard',
            params: { id: this.$path('item.project.id') },
          },
        },
        {
          title: this.$path('item.reference.name'),
          action: mergeDeepRight(BreadcrumbRouteMap[this.item.reference.__typename], {
            params: { id: this.item.reference.id },
          }),
        },
        { title: this.labels.shop },
      ]
    },
  },
  methods: {
    ...mapActions('Shop', {
      remove(dispatch, payload) {
        return dispatch('remove', { id: this.id }).then(() => this.$emit('dataguard/reset'))
      },
      fetch(dispatch) {
        return dispatch('fetch', [{ id: this.id }, { query: 'ShopDashboard' }]).then(() => {
          if (!this.item.id) return this.$router.replace({ name: 'notfound' })
        })
      },
    }),
    resend() {
      this.resendPromise = ContractApi.resend({ id: this.contract.id })
    },
  },
  metaInfo() {
    return {
      title: this.item.id ? `${this.$path('item.reference.name')} - ${this.labels.shop}` : this.labels.loading,
    }
  },
  components: {
    Card,
    HeaderComponent,
    DropdownComponent,
    RemoveComponent,
    Alert,
    Actions,
    TaskButtonList,
    ...Widgets,
  },
}
</script>

<style lang="scss">
.shop {
  background-color: $background-grey;

  &__previews {
    &-alert {
      min-width: 300px;
    }
  }

  &__content {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: max-content;
    grid-gap: 2rem 2rem;
    align-items: flex-start;

    .shop-dashboard-widget-signing > .card__header,
    .shop-dashboard-widget-summary > .card__header {
      min-height: 125px;
    }
  }
}
</style>
